import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Tag, Image, Row, Col, Statistic, Typography } from "antd";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { LineWave } from "react-loader-spinner";
import { Link } from "react-router-dom";
import {
  DollarCircleOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import "./Styles.css";

const { Title, Text } = Typography;

const CollateralRegister = () => {
  const { company } = useContext(AppContext);
  const [collateral, setCollateral] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");

  const statusColorMap = {
    "Pending Verification": "orange",
    Active: "green",
    Sold: "red",
    Released: "blue",
  };

  useEffect(() => {
    const fetchCollateral = async () => {
      try {
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/collateral_register?filter[company]=${company.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const formattedData = response.data.data.map((item) => ({
          ...item,
          documents: JSON.parse(item.documents), // Parse the documents string
          registrationDate: new Date().toISOString().split("T")[0], // Add missing date field
        }));
        setCollateral(formattedData);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching collateral:", err);
        setError(err);
        setLoading(false);
      }
    };

    fetchCollateral();
  }, [token]);

  const columns = [
    {
      title: "Collateral",
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <Link to={`/collateral/${record.id}`}>
          <div className="collateral-description">
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary">ID: {record.id}</Text>
          </div>
        </Link>
      ),
    },
    {
      title: "Value (ZMW)",
      dataIndex: "valuation_amount",
      key: "valuation_amount",
      render: (valuation_amount) => (
        <Text strong style={{ color: "#1890ff" }}>
          {valuation_amount?.toLocaleString() || "N/A"}
        </Text>
      ),
      sorter: (a, b) => (a.valuation_amount || 0) - (b.valuation_amount || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColorMap[status]} key={status}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "Pending Verification", value: "Pending Verification" },
        { text: "Verified", value: "Verified" },
        { text: "Released", value: "Released" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Images",
      dataIndex: "documents",
      key: "documents",
      render: (documents) => (
        <Image.PreviewGroup>
          <div className="collateral-images">
            {documents?.map((img, index) => (
              <Image
                key={index}
                src={img}
                width={50}
                height={50}
                style={{ borderRadius: 4, marginRight: 8 }}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      ),
    },
    {
      title: "Loan ID",
      dataIndex: "loan",
      key: "loan",
      render: (loan) => (loan ? <Tag color="blue">{loan}</Tag> : "N/A"),
    },
    {
      title: "Registered Date",
      dataIndex: "registrationDate",
      key: "registrationDate",
      render: (date) => date || "N/A",
      sorter: (a, b) =>
        new Date(a.registrationDate) - new Date(b.registrationDate),
    },
  ];

  const getSummaryStats = () => {
    const totalValue = collateral.reduce(
      (sum, item) => sum + item.valuation_amount,
      0
    );
    const activeCollateral = collateral.filter(
      (item) => item.status === "active"
    ).length;
    const releasedCollateral = collateral.filter(
      (item) => item.status === "released"
    ).length;

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Collateral Value"
              value={totalValue}
              prefix={<DollarCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
              formatter={(value) => `ZMW ${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Collateral"
              value={activeCollateral}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Released Collateral"
              value={releasedCollateral}
              prefix={<FileDoneOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <div className="center-loader">
        <LineWave
          visible={true}
          height="100"
          width="100"
          color="#36454F"
          ariaLabel="line-wave-loading"
        />
      </div>
    );
  }

  if (error) {
    return <div>Error loading collateral data: {error.message}</div>;
  }

  return (
    <div className="collateral-register">
      <Title level={3} style={{ marginBottom: 24 }}>
        <PictureOutlined /> Collateral Register
      </Title>

      {collateral && getSummaryStats()}

      <Card
        title="All Collateral Assets"
        bordered={false}
        headStyle={{ borderBottom: "1px solid #f0f0f0" }}
      >
        <Table
          columns={columns}
          dataSource={collateral}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="collateral-details">
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Detailed Description:</Text>
                    <p>{record.description}</p>
                    <Text strong>Valuation Amount:</Text>
                    <p>ZMW {record.valuation_amount?.toLocaleString()}</p>
                  </Col>
                  <Col span={12}>
                    <Text strong>Documents:</Text>
                    <Image.PreviewGroup>
                      <Row gutter={[8, 8]}>
                        {record.documents?.map((img, index) => (
                          <Col span={8} key={index}>
                            <Image src={img} style={{ borderRadius: 4 }} />
                          </Col>
                        ))}
                      </Row>
                    </Image.PreviewGroup>
                  </Col>
                </Row>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default CollateralRegister;
